// LearnCore Frontend - 布局组件

import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import {
  BookOpenIcon,
  UserCircleIcon,
  HomeIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  GlobeAltIcon,
  ChevronDownIcon,
  ClockIcon,
  Bars3Icon,
  XMarkIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  GiftIcon,
  CalendarIcon,
  UsersIcon
} from '@heroicons/react/24/outline';

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState<{ nickname?: string; avatar_url?: string } | null>(null);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('登出失败:', error);
    }
  };

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  useEffect(() => {
    let isMounted = true;
    const fetchSettings = async () => {
      if (!user) {
        setProfile(null);
        return;
      }
      try {
        const resp = await apiClient.getUserSettings();
        if (!isMounted) return;
        setProfile(resp.settings.profile || null);
      } catch (e) {
        // 忽略错误，使用邮箱兜底
      }
    };
    fetchSettings();
    return () => {
      isMounted = false;
    };
  }, [user]);

  // 点击外部关闭菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 导航栏 */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo和主导航 */}
            <div className="flex items-center">
              <Link 
                to="/" 
                className="flex items-center space-x-2 text-xl font-bold text-primary-600 hover:text-primary-700"
              >
                <BookOpenIcon className="h-8 w-8" />
                <span>LearnCore</span>
              </Link>

              {/* 公开导航 - 所有用户都可以看到 */}
              <div className="hidden lg:flex items-center space-x-6 ml-6">
                <Link
                  to="/marketplace"
                  className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                    isActivePath('/marketplace')
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                  }`}
                >
                  <GlobeAltIcon className="h-4 w-4" />
                  <span>学习集市场</span>
                </Link>

                {user && (
                  <>
                    <Link
                      to="/dashboard"
                      className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                        isActivePath('/dashboard')
                          ? 'text-primary-600 bg-primary-50'
                          : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                      }`}
                    >
                      <HomeIcon className="h-4 w-4" />
                      <span>我的学习集</span>
                    </Link>
                    
                    <Link
                      to="/passages"
                      className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                        isActivePath('/passages')
                          ? 'text-primary-600 bg-primary-50'
                          : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                      }`}
                    >
                      <DocumentTextIcon className="h-4 w-4" />
                      <span>英语阅读</span>
                    </Link>
                    
                    <Link
                      to="/classical-chinese"
                      className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                        isActivePath('/classical-chinese')
                          ? 'text-primary-600 bg-primary-50'
                          : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                      }`}
                    >
                      <BookOpenIcon className="h-4 w-4" />
                      <span>文言阅读</span>
                    </Link>

                    {/* 任务和奖励系统导航 */}
                    <Link
                      to="/tasks"
                      className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                        isActivePath('/tasks')
                          ? 'text-primary-600 bg-primary-50'
                          : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                      }`}
                    >
                      <CheckCircleIcon className="h-4 w-4" />
                      <span>任务管理</span>
                    </Link>
                     
                    <Link
                      to="/rewards"
                      className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                        isActivePath('/rewards')
                          ? 'text-primary-600 bg-primary-50'
                          : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                      }`}
                    >
                      <GiftIcon className="h-4 w-4" />
                      <span>奖励中心</span>
                    </Link>
                     
                    <Link
                      to="/calendar"
                      className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                        isActivePath('/calendar')
                          ? 'text-primary-600 bg-primary-50'
                          : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                      }`}
                    >
                      <CalendarIcon className="h-4 w-4" />
                      <span>学习日历</span>
                    </Link>

                    {/* 只有非孩子用户才显示家长管理入口 */}
                    {user?.user_type !== 'child' && (
                      <Link
                        to="/parent"
                        className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                          isActivePath('/parent')
                            ? 'text-primary-600 bg-primary-50'
                            : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                        }`}
                      >
                        <UsersIcon className="h-4 w-4" />
                        <span>家长管理</span>
                      </Link>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* 用户菜单 */}
            <div className="flex items-center space-x-4">
              {/* 移动端和平板汉堡菜单 */}
              <div className="lg:hidden" ref={mobileMenuRef}>
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="flex items-center px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 transition-colors"
                >
                  {isMobileMenuOpen ? (
                    <XMarkIcon className="h-5 w-5" />
                  ) : (
                    <Bars3Icon className="h-5 w-5" />
                  )}
                </button>

                {/* 移动端导航菜单 */}
                {isMobileMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                    <div className="py-1">
                      <Link
                        to="/marketplace"
                        className={`flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                          isActivePath('/marketplace')
                            ? 'text-primary-600 bg-primary-50'
                            : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                        }`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <GlobeAltIcon className="h-4 w-4" />
                        <span>学习集市场</span>
                      </Link>

                      {user && (
                        <>
                          <Link
                            to="/dashboard"
                            className={`flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                              isActivePath('/dashboard')
                                ? 'text-primary-600 bg-primary-50'
                                : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                            }`}
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <HomeIcon className="h-4 w-4" />
                            <span>我的学习集</span>
                          </Link>
                          
                          <Link
                            to="/passages"
                            className={`flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                              isActivePath('/passages')
                                ? 'text-primary-600 bg-primary-50'
                                : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                            }`}
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <DocumentTextIcon className="h-4 w-4" />
                            <span>英语阅读</span>
                          </Link>
                          
                          <Link
                            to="/classical-chinese"
                            className={`flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                              isActivePath('/classical-chinese')
                                ? 'text-primary-600 bg-primary-50'
                                : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                            }`}
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <BookOpenIcon className="h-4 w-4" />
                            <span>文言阅读</span>
                          </Link>

                          {/* 任务和奖励系统移动端导航 */}
                          <Link
                            to="/tasks"
                            className={`flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                              isActivePath('/tasks')
                                ? 'text-primary-600 bg-primary-50'
                                : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                            }`}
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <CheckCircleIcon className="h-4 w-4" />
                            <span>任务管理</span>
                          </Link>
                           
                          <Link
                            to="/rewards"
                            className={`flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                              isActivePath('/rewards')
                                ? 'text-primary-600 bg-primary-50'
                                : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                            }`}
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <GiftIcon className="h-4 w-4" />
                            <span>奖励中心</span>
                          </Link>
                           
                          <Link
                            to="/calendar"
                            className={`flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                              isActivePath('/calendar')
                                ? 'text-primary-600 bg-primary-50'
                                : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                            }`}
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <CalendarIcon className="h-4 w-4" />
                            <span>学习日历</span>
                          </Link>

                          {/* 只有非孩子用户才显示家长管理入口 */}
                          {user?.user_type !== 'child' && (
                            <Link
                              to="/parent"
                              className={`flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                                isActivePath('/parent')
                                  ? 'text-primary-600 bg-primary-50'
                                  : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                              }`}
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              <UsersIcon className="h-4 w-4" />
                              <span>家长管理</span>
                            </Link>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {user ? (
                <div className="relative" ref={userMenuRef}>
                  {/* 桌面端用户信息显示 - 简化为头像和下拉菜单 */}
                  <div className="hidden lg:block">
                    <button
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                      className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 transition-colors"
                    >
                      {profile?.avatar_url ? (
                        <img src={profile.avatar_url} alt="avatar" className="h-8 w-8 rounded-full object-cover border" />
                      ) : (
                        <UserCircleIcon className="h-8 w-8" />
                      )}
                      <span className="max-w-24 truncate">{profile?.nickname || user.email}</span>
                      <ChevronDownIcon className={`h-4 w-4 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* 桌面端下拉菜单 */}
                    {isUserMenuOpen && (
                      <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                        <div className="py-1">
                          <Link
                            to="/study-time"
                            className={`flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                              isActivePath('/study-time')
                                ? 'text-primary-600 bg-primary-50'
                                : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                            }`}
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <ClockIcon className="h-4 w-4" />
                            <span>学习时间</span>
                          </Link>
                          
                          <Link
                            to="/user/center"
                            className={`flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                              isActivePath('/user/center')
                                ? 'text-primary-600 bg-primary-50'
                                : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                            }`}
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <Cog6ToothIcon className="h-4 w-4" />
                            <span>设置</span>
                          </Link>

                          {/* 任务和奖励系统桌面端下拉菜单 */}
                          <Link
                            to="/tasks"
                            className={`flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                              isActivePath('/tasks')
                                ? 'text-primary-600 bg-primary-50'
                                : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                            }`}
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <CheckCircleIcon className="h-4 w-4" />
                            <span>任务管理</span>
                          </Link>
                           
                          <Link
                            to="/rewards"
                            className={`flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                              isActivePath('/rewards')
                                ? 'text-primary-600 bg-primary-50'
                                : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                            }`}
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <GiftIcon className="h-4 w-4" />
                            <span>奖励中心</span>
                          </Link>
                           
                          <Link
                            to="/calendar"
                            className={`flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                              isActivePath('/calendar')
                                ? 'text-primary-600 bg-primary-50'
                                : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                            }`}
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <CalendarIcon className="h-4 w-4" />
                            <span>学习日历</span>
                          </Link>

                          {/* 只有非孩子用户才显示家长管理入口 */}
                          {user?.user_type !== 'child' && (
                            <Link
                              to="/parent"
                              className={`flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                                isActivePath('/parent')
                                  ? 'text-primary-600 bg-primary-50'
                                  : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                              }`}
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              <UsersIcon className="h-4 w-4" />
                              <span>家长管理</span>
                            </Link>
                          )}
                           
                          <button
                            onClick={() => {
                              setIsUserMenuOpen(false);
                              handleLogout();
                            }}
                            className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 hover:text-danger-600 hover:bg-gray-50 transition-colors"
                          >
                            <ArrowRightOnRectangleIcon className="h-4 w-4" />
                            <span>登出</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 移动端和平板用户菜单 */}
                  <div className="lg:hidden">
                    <button
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                      className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 transition-colors"
                    >
                      {profile?.avatar_url ? (
                        <img src={profile.avatar_url} alt="avatar" className="h-6 w-6 rounded-full object-cover border" />
                      ) : (
                        <UserCircleIcon className="h-5 w-5" />
                      )}
                      <span className="max-w-20 truncate">{profile?.nickname || user.email}</span>
                      <ChevronDownIcon className={`h-4 w-4 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* 下拉菜单 */}
                    {isUserMenuOpen && (
                      <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                        <div className="py-1">
                          <Link
                            to="/study-time"
                            className={`flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                              isActivePath('/study-time')
                                ? 'text-primary-600 bg-primary-50'
                                : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                            }`}
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <ClockIcon className="h-4 w-4" />
                            <span>学习时间</span>
                          </Link>
                          
                          <Link
                            to="/user/center"
                            className={`flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                              isActivePath('/user/center')
                                ? 'text-primary-600 bg-primary-50'
                                : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                            }`}
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <Cog6ToothIcon className="h-4 w-4" />
                            <span>设置</span>
                          </Link>

                          {/* 任务和奖励系统移动端下拉菜单 */}
                          <Link
                            to="/tasks"
                            className={`flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                              isActivePath('/tasks')
                                ? 'text-primary-600 bg-primary-50'
                                : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                            }`}
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <CheckCircleIcon className="h-4 w-4" />
                            <span>任务管理</span>
                          </Link>
                           
                          <Link
                            to="/rewards"
                            className={`flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                              isActivePath('/rewards')
                                ? 'text-primary-600 bg-primary-50'
                                : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                            }`}
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <GiftIcon className="h-4 w-4" />
                            <span>奖励中心</span>
                          </Link>
                           
                          <Link
                            to="/calendar"
                            className={`flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                              isActivePath('/calendar')
                                ? 'text-primary-600 bg-primary-50'
                                : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                            }`}
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <CalendarIcon className="h-4 w-4" />
                            <span>学习日历</span>
                          </Link>

                          {/* 只有非孩子用户才显示家长管理入口 */}
                          {user?.user_type !== 'child' && (
                            <Link
                              to="/parent"
                              className={`flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                                isActivePath('/parent')
                                  ? 'text-primary-600 bg-primary-50'
                                  : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                              }`}
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              <UsersIcon className="h-4 w-4" />
                              <span>家长管理</span>
                            </Link>
                          )}
                           
                          <button
                            onClick={() => {
                              setIsUserMenuOpen(false);
                              handleLogout();
                            }}
                            className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 hover:text-danger-600 hover:bg-gray-50 transition-colors"
                          >
                            <ArrowRightOnRectangleIcon className="h-4 w-4" />
                            <span>登出</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center space-x-4">
                  <Link
                    to="/login"
                    className="text-gray-700 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    登录
                  </Link>
                  <Link
                    to="/register"
                    className="btn-primary text-sm"
                  >
                    注册
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* 主内容区域 */}
      <main className="flex-1">
        {children}
      </main>

      {/* 页脚 */}
      <footer className="bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <BookOpenIcon className="h-4 w-4" />
              <span>© 2025 LearnCore MVP. 基于Cloudflare构建.</span>
            </div>
            <div className="text-sm text-gray-500">
              版本 0.1.0
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
